import React, { useContext, useState } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  ItemsContext_MultiOrderGuideItemsQuery,
  ItemsContext_OrderGuideItemFieldsFragment,
  ItemsContext_OrderGuideRestaurantCollectionFieldsFragment,
} from '@/types';

interface OrderGuideState {
  /**
   * Whether the store has been hydrated with data
   */
  isHydrated: boolean;

  /**
   * Array of order guide items.
   */
  orderGuideItems: ItemsContext_OrderGuideItemFieldsFragment[];

  /**
   * Order guide restaurant collections. Supplier collections can't be modified by the app.
   */
  restaurantCollections: ItemsContext_OrderGuideRestaurantCollectionFieldsFragment[];
}

interface OrderGuideActions {
  /**
   * Add an item to the order guide
   */
  addItem: (item: ItemsContext_OrderGuideItemFieldsFragment) => void;

  /**
   * Add a new restaurant collection
   */
  addRestaurantCollection: (
    collection: ItemsContext_OrderGuideRestaurantCollectionFieldsFragment,
  ) => void;

  /**
   * Add a restaurant collection at most once (by UUID).
   * No-op when the collection is already in the store, avoiding unnecessary re-renders.
   *
   * @param collection - The collection to add
   */
  addRestaurantCollectionOnce: (
    collection: ItemsContext_OrderGuideRestaurantCollectionFieldsFragment,
  ) => void;

  /**
   * Hydrate the store with data from the query or offline storage
   */
  hydrate: (data: ItemsContext_MultiOrderGuideItemsQuery) => void;

  /**
   * Remove an item from the order guide
   *
   * @param variantPackUUID - The UUID of the variant pack to remove
   * @param partitionUUID - The partition UUID (required for OGR items, optional for OGS)
   */
  removeItem: (variantPackUUID: string, partitionUUID?: string) => void;

  /**
   * Remove a restaurant collection from the store
   *
   * @param collectionUUID - The UUID of the collection to remove
   */
  removeRestaurantCollection: (collectionUUID: string) => void;

  /**
   * Reset order guide restaurant state after order submission
   * Clears memo, notes, request based on persist flags
   */
  resetOrderGuideRestaurantState: () => void;

  /**
   * Restore state from a snapshot (for rollback on error)
   */
  restore: (snapshot: OrderGuideState) => void;

  /**
   * Set the restaurant collections (used for reordering)
   *
   * @param collections - The new ordered array of collections
   */
  setRestaurantCollections: (
    collections: ItemsContext_OrderGuideRestaurantCollectionFieldsFragment[],
  ) => void;

  /**
   * Create a snapshot of the current state for rollback
   */
  snapshot: () => OrderGuideState;

  /**
   * Update item notes/request
   *
   * @param orderGuideItemId - The order_guide_item_id of the item to update
   * @param updatedItem - The updated item from the mutation response
   */
  updateItem: (
    orderGuideItemId: string,
    updatedItem: ItemsContext_OrderGuideItemFieldsFragment,
  ) => void;

  /**
   * Update multiple item dispositions (optimistic update)
   *
   * @param updates - Array of updated items from the mutation response
   * @param variantPackToPartitionsMap - Map of variant pack UUID to set of partition UUIDs that were updated
   * @param defaultPartitionUUID - The default partition UUID for the selected collection
   */
  updateItemDispositions: (
    updates: ItemsContext_OrderGuideItemFieldsFragment[],
    variantPackToPartitionsMap: { [variantPackUUID: string]: Set<string> },
    defaultPartitionUUID: string,
  ) => void;

  /**
   * Update a restaurant collection (e.g., for renaming)
   *
   * @param collectionUUID - The UUID of the collection to update
   * @param updates - The fields to update on the collection
   */
  updateRestaurantCollection: (
    collectionUUID: string,
    updates: Partial<ItemsContext_OrderGuideRestaurantCollectionFieldsFragment>,
  ) => void;
}

export type OrderGuideStore = OrderGuideState & OrderGuideActions;

export type OrderGuideStoreApi = StoreApi<OrderGuideStore>;

/**
 * Selector to check if an item is in the order guide
 */
export const selectIsInOrderGuide = (
  state: OrderGuideState,
  variantPackUUID: string,
): boolean => {
  return state.orderGuideItems.some(
    (item) => item.variant_pack?.uuid === variantPackUUID,
  );
};

/**
 * Creates an isolated order guide store instance.
 * Each provider mount gets its own store — no cross-talk between providers.
 */
const createOrderGuideStore = (): OrderGuideStoreApi =>
  createStore<OrderGuideStore>()(
    immer((set, get) => ({
      addItem: (item: ItemsContext_OrderGuideItemFieldsFragment) => {
        set((state) => {
          state.orderGuideItems.push(item);
        });
      },

      addRestaurantCollection: (
        collection: ItemsContext_OrderGuideRestaurantCollectionFieldsFragment,
      ) => {
        set((state) => {
          state.restaurantCollections.unshift(collection);
        });
      },

      addRestaurantCollectionOnce: (
        collection: ItemsContext_OrderGuideRestaurantCollectionFieldsFragment,
      ) => {
        const alreadyExists = get().restaurantCollections.some(
          (c) => c.uuid === collection.uuid,
        );
        if (!alreadyExists) {
          set((state) => {
            state.restaurantCollections.unshift(collection);
          });
        }
      },

      hydrate: (data: ItemsContext_MultiOrderGuideItemsQuery) => {
        set((state) => {
          state.orderGuideItems = data.getOrderGuideItems;
          state.restaurantCollections = data.order_guide_restaurant_collection;
          state.isHydrated = true;
        });
      },

      isHydrated: false,

      orderGuideItems: [],

      removeItem: (variantPackUUID: string, partitionUUID?: string) => {
        set((state) => {
          state.orderGuideItems = state.orderGuideItems.filter((ogi) => {
            const orderGuideRestaurant = ogi.order_guide_restaurant;
            // If it's an OGR, check both partition UUID and variant pack UUID
            if (orderGuideRestaurant) {
              return (
                orderGuideRestaurant.order_guide_restaurant_partition_uuid !==
                  partitionUUID || ogi.variant_pack?.uuid !== variantPackUUID
              );
            }
            // If it's an OGS, there can only be 1 per VP, so delete it if it matches
            return ogi.variant_pack?.uuid !== variantPackUUID;
          });
        });
      },

      removeRestaurantCollection: (collectionUUID: string) => {
        set((state) => {
          state.restaurantCollections = state.restaurantCollections.filter(
            (collection) => collection.uuid !== collectionUUID,
          );
        });
      },

      resetOrderGuideRestaurantState: () => {
        set((state) => {
          state.orderGuideItems = state.orderGuideItems.map((ogi) => {
            const orderGuideRestaurant = ogi.order_guide_restaurant;
            if (!orderGuideRestaurant) {
              return ogi;
            }
            return {
              ...ogi,
              order_guide_restaurant: {
                ...orderGuideRestaurant,
                is_minimized: false,
                memo: orderGuideRestaurant.persist_memo
                  ? orderGuideRestaurant.memo
                  : null,
                notes: orderGuideRestaurant.persist_notes
                  ? orderGuideRestaurant.notes
                  : null,
                request: orderGuideRestaurant.persist_request
                  ? orderGuideRestaurant.request
                  : null,
              },
            };
          });
        });
      },

      restaurantCollections: [],

      restore: (snapshot: OrderGuideState) => {
        set((state) => {
          state.isHydrated = snapshot.isHydrated;
          state.orderGuideItems = snapshot.orderGuideItems;
          state.restaurantCollections = snapshot.restaurantCollections;
        });
      },

      setRestaurantCollections: (
        collections: ItemsContext_OrderGuideRestaurantCollectionFieldsFragment[],
      ) => {
        set((state) => {
          state.restaurantCollections = collections;
        });
      },

      snapshot: (): OrderGuideState => {
        const state = get();
        return {
          isHydrated: state.isHydrated,
          orderGuideItems: [...state.orderGuideItems],
          restaurantCollections: [...state.restaurantCollections],
        };
      },

      updateItem: (
        orderGuideItemId: string,
        updatedItem: ItemsContext_OrderGuideItemFieldsFragment,
      ) => {
        set((state) => {
          const index = state.orderGuideItems.findIndex(
            (ogi) => ogi.order_guide_item_id === orderGuideItemId,
          );
          if (index !== -1) {
            state.orderGuideItems[index] = {
              ...updatedItem,
              variant_pack: state.orderGuideItems[index].variant_pack,
            };
          }
        });
      },

      updateItemDispositions: (
        updates: ItemsContext_OrderGuideItemFieldsFragment[],
        variantPackToPartitionsMap: {
          [variantPackUUID: string]: Set<string>;
        },
        defaultPartitionUUID: string,
      ) => {
        set((state) => {
          const updatedUUIDToItemMap = updates.reduce<{
            [ogrUUID: string]: ItemsContext_OrderGuideItemFieldsFragment;
          }>((acc, resp) => {
            acc[resp.order_guide_item_id] = resp;
            return acc;
          }, {});

          const variantPackUUIDToOrderGuideRestaurantMap = updates.reduce<{
            [
              variantPackUUID: string
            ]: ItemsContext_OrderGuideItemFieldsFragment;
          }>((acc, resp) => {
            const variantPackUUID = resp.variant_pack?.uuid;
            if (variantPackUUID) {
              acc[variantPackUUID] = resp;
            }
            return acc;
          }, {});

          state.orderGuideItems = state.orderGuideItems.map((ogi) => {
            const variantPackUUID = ogi.variant_pack?.uuid;
            const partitionUUID =
              ogi.order_guide_item_partition_id || defaultPartitionUUID;

            if (!variantPackUUID) {
              return ogi;
            }

            const wasInRequest =
              variantPackToPartitionsMap[variantPackUUID]?.has(partitionUUID);

            if (!wasInRequest) {
              return ogi;
            }

            const updatedOrderGuideRestaurantItem =
              updatedUUIDToItemMap[ogi.order_guide_item_id];
            if (updatedOrderGuideRestaurantItem) {
              return updatedOrderGuideRestaurantItem;
            }

            const newOrderGuideRestaurant =
              variantPackUUIDToOrderGuideRestaurantMap[variantPackUUID];
            if (newOrderGuideRestaurant) {
              return newOrderGuideRestaurant;
            }

            return ogi;
          });
        });
      },

      updateRestaurantCollection: (
        collectionUUID: string,
        updates: Partial<ItemsContext_OrderGuideRestaurantCollectionFieldsFragment>,
      ) => {
        set((state) => {
          const index = state.restaurantCollections.findIndex(
            (collection) => collection.uuid === collectionUUID,
          );
          if (index !== -1) {
            state.restaurantCollections[index] = {
              ...state.restaurantCollections[index],
              ...updates,
            };
          }
        });
      },
    })),
  );

const OrderGuideStoreContext = React.createContext<OrderGuideStoreApi | null>(
  null,
);

/**
 * Provides an isolated order guide store instance to the component tree.
 * Place above both NewOrderActionsContextProvider and
 * MultiOrderGuideItemsContextProvider so all consumers share the same
 * scoped store.
 */
export const OrderGuideStoreProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [store] = useState(() => createOrderGuideStore());

  return (
    <OrderGuideStoreContext.Provider value={store}>
      {children}
    </OrderGuideStoreContext.Provider>
  );
};

/**
 * Subscribe to order guide store state with a selector.
 */
export const useOrderGuideStore = <T,>(
  selector: (state: OrderGuideStore) => T,
): T => {
  const store = useContext(OrderGuideStoreContext);
  if (!store) {
    throw new Error(
      'useOrderGuideStore must be used within an OrderGuideStoreProvider',
    );
  }
  return useStore(store, selector);
};

/**
 * Get the store API for imperative access (e.g., in callbacks).
 */
export const useOrderGuideStoreApi = (): OrderGuideStoreApi => {
  const store = useContext(OrderGuideStoreContext);
  if (!store) {
    throw new Error(
      'useOrderGuideStoreApi must be used within an OrderGuideStoreProvider',
    );
  }
  return store;
};
